import {
  Component,
  OnInit,
  OnChanges,
  Inject,
  Optional,
  Input,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ConfirmationService, MessageService, TreeNode } from 'primeng/api';
import {
  MenuModel,
  RolesPermissionsService,
  AplicacionModel,
  RolModel,
  PermisoModel,
  MenuOptionsModel,
} from './roles-permissions.service';
import {
  ROLES_PERMISSIONS_TRANSLATE,
  RolesPermissionsTranslateFn,
} from './rest-api.token';

export interface AppData extends AplicacionModel {}
export interface MenuData {
  Id: number;
  Nombre: string;
}
export interface MenuTreeNode extends TreeNode {
  data?: MenuOptionsModel;
}
export interface RoleData extends RolModel {}
export interface PermissionData extends PermisoModel {}

@Component({
  selector: 'eco-maestro-roles-permissions',
  templateUrl: './roles-permissions.component.html',
  styleUrls: ['./roles-permissions.component.scss'],
})
export class RolesPermissionsComponent implements OnInit, OnChanges {
  loader = false;
  private pendingLoaders = 0;
  private initialized = false;

  appData: AppData[] = [];
  menuData: MenuData[] = [];
  menuTree: MenuTreeNode[] = [];
  rolesData: RoleData[] = [];
  permissionsData: PermissionData[] = [];
  selectedExportPermId: number | null = null;
  currentPermissionsByMenu: { IdRol: number; IdPermiso: number }[] = [];
  form: FormGroup;

  selectedLeafId: number | null = null;
  selectedNodes: TreeNode[] = [];
  activeMenuNode: TreeNode | null = null;
  nodeLookup = new Map<string, TreeNode>();
  permissionsMap = new Map<string, boolean>();

  permissionMatrix: Record<number, Record<number, boolean>> = {};

  allNode: TreeNode = {
    key: 'all',
    label: 'Todos',
    children: this.menuTree,
    data: { OpcionOriginalId: null },
    parent: null,
  };
  allSelected = false;
  menus: MenuModel[] = [];
  menusAplicaciones: any[] = [];
  menusEmpresas: any[] = [];

  @Input() readOnly = false;
  @Input() applicationId: number | null = null;
  @Input() menuId: number | null = null;

  get selectorsReadOnly(): boolean {
    return this.readOnly || this.applicationId != null || this.menuId != null;
  }

  async exportPermissionsExcel(): Promise<void> {
    const appId = Number(this.form.get('appSelect')?.value);
    const menuId = this.form.get('menuSelect')?.value;

    if (!appId || !menuId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Seleccione una aplicación y un menú',
      });
      return;
    }

    // Use selected permission from export menu if provided, otherwise fallback to 'view'
    let permisoIdToUse: number | null = this.selectedExportPermId;
    if (!permisoIdToUse) {
      const permisoView = this.permissionsData.find((p) => {
        const nombre = String(p.Nombre || '').toLowerCase();
        const acr = String(p.Acronimo || '').toLowerCase();
        return nombre === 'view' || acr === 'view';
      });
      permisoIdToUse = permisoView ? permisoView.Id : null;
    }
    if (!permisoIdToUse) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se encontró un permiso válido para exportar',
      });
      return;
    }

    try {
      this.startLoading();
      const response = await this.rolesPermissionsService.exportPermisosExcel(
        appId,
        menuId,
        permisoIdToUse,
      );

      if (!response || !response.body) {
        throw new Error('Respuesta vacía del servidor');
      }

      const blob = response.body as Blob;

      // Try to parse filename from headers
      let filename = 'permisos.xlsx';
      const contentDisposition =
        response.headers?.get('content-disposition') ||
        response.headers?.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename\*=UTF-8''(.+)$|filename="?([^";]+)"?/i,
        );
        if (match) {
          filename = decodeURIComponent(match[1] || match[2] || filename);
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting permissions', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo exportar los permisos',
      });
    } finally {
      this.stopLoading();
    }
  }

  get selectedMenuLabel(): string {
    const selectedMenuId = Number(this.form.get('menuSelect')?.value);
    if (!selectedMenuId) {
      return '';
    }

    const selectedMenu = this.menuData.find(
      (menu) => menu.Id === selectedMenuId,
    );
    return selectedMenu?.Nombre ?? '';
  }

  constructor(
    private readonly rolesPermissionsService: RolesPermissionsService,
    private readonly fb: FormBuilder,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
    @Optional()
    @Inject(ROLES_PERMISSIONS_TRANSLATE)
    private readonly translateFn?: RolesPermissionsTranslateFn,
  ) {
    this.form = this.fb.group({
      appSelect: [0],
      menuSelect: [{ value: 0, disabled: true }],
    });
  }

  ngOnInit(): void {
    this.form.get('appSelect')?.valueChanges.subscribe((appId) => {
      if (!appId) {
        return;
      }

      this.form.get('menuSelect')?.reset(null, { emitEvent: false });
      this.menuTree = [];
      this.selectedNodes = [];
      this.selectedLeafId = null;
      this.activeMenuNode = null;
      this.allSelected = false;
      this.currentPermissionsByMenu = [];

      this.getMenuData(String(appId));
      void this.getRolesData(String(appId));
    });

    this.form.get('menuSelect')?.valueChanges.subscribe((menuId) => {
      if (!menuId) {
        this.menuTree = [];
        this.selectedNodes = [];
        this.selectedLeafId = null;
        this.activeMenuNode = null;
        this.allSelected = false;
        return;
      }

      void this.handleMenuChange(Number(menuId));
    });

    this.startLoading();
    void this.initialize().finally(() => {
      this.stopLoading();
      this.initialized = true;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) {
      return;
    }

    const hasSelectorInputsChange =
      'readOnly' in changes ||
      'applicationId' in changes ||
      'menuId' in changes;

    if (!hasSelectorInputsChange) {
      return;
    }

    if (this.applicationId != null) {
      this.form.get('appSelect')?.setValue(this.applicationId, {
        emitEvent: false,
      });
      this.getMenuData(String(this.applicationId));
      void this.getRolesData(String(this.applicationId));
    }

    if (this.menuId != null) {
      this.form.get('menuSelect')?.setValue(this.menuId, { emitEvent: false });
      void this.handleMenuChange(this.menuId);
    }

    this.applySelectorsReadOnlyState();
  }

  private async initialize(): Promise<void> {
    await this.getAppsData();

    if (this.applicationId != null) {
      this.form.get('appSelect')?.setValue(this.applicationId, {
        emitEvent: false,
      });
    }

    await this.getPermisosData();
    await this.getMenus();

    if (this.menuId != null) {
      this.form.get('menuSelect')?.setValue(this.menuId, { emitEvent: false });
    }

    const appId = this.form.get('appSelect')?.value;
    if (appId) {
      await this.getRolesData(String(appId));
    }

    const selectedMenuId = this.form.get('menuSelect')?.value;
    if (selectedMenuId) {
      await this.handleMenuChange(Number(selectedMenuId));
    }

    this.applySelectorsReadOnlyState();
  }

  private applySelectorsReadOnlyState(): void {
    const appControl = this.form.get('appSelect');
    const menuControl = this.form.get('menuSelect');

    if (this.selectorsReadOnly) {
      appControl?.disable({ emitEvent: false });
      menuControl?.disable({ emitEvent: false });
      return;
    }

    appControl?.enable({ emitEvent: false });

    if (this.menuData.length > 0) {
      menuControl?.enable({ emitEvent: false });
    } else {
      menuControl?.disable({ emitEvent: false });
    }
  }

  private startLoading(): void {
    this.pendingLoaders += 1;
    this.loader = true;
  }

  confirmExport(panel: any): void {
    panel.hide();
    void this.exportPermissionsExcel();
  }

  openExportPanel(event: Event, panel: any): void {
    const btn = (event.currentTarget || event.target) as HTMLElement;
    panel.toggle(event);
    // after overlay is in DOM, compute arrow position so it points to the button center
    setTimeout(() => {
      try {
        const overlayEl = document.querySelector(
          '.export-overlay',
        ) as HTMLElement;
        if (!overlayEl) return;
        const btnRect = btn.getBoundingClientRect();
        const overlayRect = overlayEl.getBoundingClientRect();
        const arrowLeft = Math.round(
          btnRect.left + btnRect.width / 2 - overlayRect.left,
        );
        overlayEl.style.setProperty('--overlayArrowLeft', `${arrowLeft}px`);
      } catch (e) {
        // ignore
      }
    }, 0);
  }

  private stopLoading(): void {
    this.pendingLoaders = Math.max(0, this.pendingLoaders - 1);
    this.loader = this.pendingLoaders > 0;
  }

  private async withLoader<T>(action: () => Promise<T>): Promise<T> {
    this.startLoading();
    try {
      return await action();
    } finally {
      this.stopLoading();
    }
  }

  private async handleMenuChange(menuId: number): Promise<void> {
    await this.withLoader(async () => {
      const appId = Number(this.form.get('appSelect')?.value);
      const selectedMenu =
        await this.rolesPermissionsService.getMenuAppCompleto(menuId);

      this.menuTree = this.buildMenuTree2(selectedMenu?.Opciones || []);
      this.allNode.children = this.menuTree;
      this.activeMenuNode = null;
      this.allSelected = false;

      await this.refreshMenuSelectionState(menuId, appId);
    });
  }

  private async refreshMenuSelectionState(
    menuId: number,
    appId: number,
  ): Promise<void> {
    const selectedKeys =
      await this.rolesPermissionsService.getOpctionsWithPermisionsByMenu(
        menuId,
        appId,
      );

    this.selectAndExpandNode(selectedKeys);
  }

  async getAppsData(): Promise<void> {
    this.form.get('menuSelect')?.disable({ emitEvent: false });

    await this.withLoader(async () => {
      const data = await this.rolesPermissionsService.getApps();
      this.appData = data;

      if (this.appData.length > 0) {
        const firstApp = this.appData[0];
        this.form.get('appSelect')?.setValue(firstApp.Id, { emitEvent: false });
      }
    });

    this.applySelectorsReadOnlyState();
  }

  getMenuData(appId: string): void {
    const selectedAppId = Number(appId);

    this.form.get('menuSelect')?.disable({ emitEvent: false });
    this.menuData = [];

    this.menusAplicaciones.forEach((ma) => {
      if (ma.AplicacionId === selectedAppId) {
        const menu = this.menus.find((item) => item.Id === ma.MenuId);
        if (menu) {
          this.menuData.push({
            Id: ma.MenuId,
            Nombre: menu?.Nombre || 'Sin nombre',
          });
        }
      }
    });

    this.menusEmpresas.forEach((me) => {
      if (me.AplicacionId === selectedAppId) {
        const menu = this.menus.find((item) => item.Id === me.MenuId);
        // Los menús de empresa se añaden solo si no están ya incluidos por la relación aplicación-menú,
        // para evitar duplicados en el selector
        if (menu && !this.menuData.find((m) => m.Id === me.MenuId)) {
          this.menuData.push({
            Id: me.MenuId,
            Nombre: menu?.Nombre || 'Sin nombre',
          });
        }
      }
    });

    this.applySelectorsReadOnlyState();
    this.form
      .get('menuSelect')
      ?.setValue(this.menuData.length > 0 ? this.menuData[0].Id : null);
  }

  async getMenus(): Promise<void> {
    this.form.get('menuSelect')?.disable({ emitEvent: false });

    await this.withLoader(async () => {
      const data =
        await this.rolesPermissionsService.getMenusAplicacionesEmpresas();

      this.menus = data[0];
      this.menusAplicaciones = data[1];
      this.menusEmpresas = this.getUniqueMenusEmpresasByMenuId(data[2]);
      this.getMenuData(String(this.form.get('appSelect')?.value));
    });

    this.applySelectorsReadOnlyState();
  }

  private getUniqueMenusEmpresasByMenuId(items: any[]): any[] {
    const seen = new Set<number>();
    return items.filter((item) => {
      if (seen.has(item.MenuId)) {
        return false;
      }

      seen.add(item.MenuId);
      return true;
    });
  }

  buildMenuTree2(data: any[]): TreeNode[] {
    const map = new Map<number, TreeNode>();
    const roots: TreeNode[] = [];
    this.nodeLookup.clear();

    data.forEach((item) => {
      const node: TreeNode = {
        key: String(item.Id),
        label: item.TextoId,
        children: [],
        data: item,
        selectable: false,
      };

      map.set(item.Id, node);
      this.nodeLookup.set(String(item.Id), node);
    });

    data.forEach((item) => {
      const node = map.get(item.Id);
      if (!node) {
        return;
      }

      if (item.PadreId && map.has(item.PadreId)) {
        const parent = map.get(item.PadreId);
        if (!parent) {
          roots.push(node);
          return;
        }

        node.parent = parent;
        parent.children?.push(node);
      } else {
        roots.push(node);
      }
    });

    this.setIcons(roots);
    return roots;
  }

  onNodeSelect(event: any): void {
    const node = event.node;
    if (!node.children || node.children.length === 0) {
      this.selectedLeafId = Number(node.key);
    }
  }

  setIcons(nodes: TreeNode[]): void {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        node.icon = 'pi pi-folder';
        this.setIcons(node.children);
      } else {
        node.icon = 'pi pi-file';
      }
    });
  }

  async getRolesData(appId: string): Promise<void> {
    await this.withLoader(async () => {
      const data = await this.rolesPermissionsService.getRolesApp(
        Number(appId),
      );
      this.rolesData = data;
      this.initializeMatrix();
    });
  }

  async getPermisosData(): Promise<void> {
    await this.withLoader(async () => {
      const data = await this.rolesPermissionsService.getPermisos();
      this.permissionsData = data;
      // default selected permission for export should be 'view' when permissions load
      const permisoView = this.permissionsData.find((p) => {
        const nombre = String(p.Nombre || '').toLowerCase();
        const acr = String(p.Acronimo || '').toLowerCase();
        return nombre === 'view' || acr === 'view';
      });
      this.selectedExportPermId = permisoView ? permisoView.Id : null;
    });
  }

  private key(roleId: number, permitId: number): string {
    return `${roleId}-${permitId}`;
  }

  initializeMatrix(): void {
    this.permissionMatrix = {};

    this.rolesData.forEach((role) => {
      this.permissionMatrix[role.Id] = {};

      this.permissionsData.forEach((permit) => {
        this.permissionMatrix[role.Id][permit.Id] = false;
      });
    });

    this.currentPermissionsByMenu.forEach((permission) => {
      if (this.permissionMatrix[permission.IdRol]) {
        this.permissionMatrix[permission.IdRol][permission.IdPermiso] = true;
      }
    });
  }

  toggleAllPermissions(roleId: number, checked: boolean): void {
    if (this.readOnly) return;

    this.permissionsData.forEach((permit) => {
      this.permissionMatrix[roleId][permit.Id] = checked;
    });
  }

  areAllPermissionsChecked(roleId: number): boolean {
    return this.permissionsData.every(
      (permit) => this.permissionMatrix[roleId][permit.Id],
    );
  }

  toggleAllRolesForPermission(permissionId: number, checked: boolean): void {
    this.rolesData.forEach((role) => {
      if (role.Id == null) return;
      this.permissionMatrix[role.Id][permissionId] = checked;
    });
  }

  areAllRolesCheckedForPermission(permissionId: number): boolean {
    const validRoles = this.rolesData.filter((role) => role.Id != null);
    if (!validRoles.length) return false;

    return validRoles.every((role) => {
      if (role.Id == null) return false;
      return this.permissionMatrix[role.Id][permissionId];
    });
  }

  buildPermissionsPayload(): any[] {
    const result: any[] = [];

    Object.keys(this.permissionMatrix).forEach((roleId) => {
      Object.keys(this.permissionMatrix[roleId]).forEach((permitId) => {
        if (this.permissionMatrix[roleId][permitId]) {
          result.push({
            IdRol: Number(roleId),
            IdPermiso: Number(permitId),
          });
        }
      });
    });

    return result;
  }

  findNodeByKey(nodes: TreeNode[], key: number): TreeNode | null {
    for (const node of nodes) {
      if (node.key === `${key}`) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const found = this.findNodeByKey(node.children, key);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  expandParents(node: TreeNode): void {
    let parent = node.parent;

    while (parent) {
      parent.expanded = true;
      parent = parent.parent;
    }
  }

  findNodeByOpcionOriginalId(
    nodes: TreeNode[],
    opcionOriginalId: number,
  ): TreeNode | null {
    for (const node of nodes) {
      if (node.data?.OpcionOriginalId === opcionOriginalId) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const found = this.findNodeByOpcionOriginalId(
          node.children,
          opcionOriginalId,
        );
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  selectAndExpandNode(keys: number[]): void {
    const selectedByKey = new Map<string, TreeNode>();

    keys.forEach((key) => {
      const oldNode = this.findNodeByOpcionOriginalId(this.menuTree, key);
      const node = this.nodeLookup.get(String(oldNode?.key));

      if (!node) {
        return;
      }

      const nodesToSelect: TreeNode[] = [];
      let current: TreeNode | undefined = node;

      while (current) {
        // Auto-expand is intentionally disabled during permission sync.
        nodesToSelect.push(current);
        current = current.parent ?? undefined;
      }

      nodesToSelect.forEach((treeNode) => {
        if (treeNode.key) {
          selectedByKey.set(treeNode.key, treeNode);
        }
      });
    });

    this.selectedNodes = Array.from(selectedByKey.values());
  }

  onPermissionChange(event: any): void {
    this.buildPermissionList();
  }

  buildPermissionList(): { menuId: string | undefined; type: string }[] {
    return this.selectedNodes.map((node) => ({
      menuId: node.key,
      type: node.children?.length ? 'branch' : 'leaf',
    }));
  }

  getLeafPermissions(): (string | undefined)[] {
    return this.selectedNodes
      .filter((node) => !node.children || node.children.length === 0)
      .map((node) => node.key);
  }

  onLabelClick(node: TreeNode, event: MouseEvent): void {
    event.stopPropagation();
    this.allSelected = false;

    if (!node.children || node.children.length === 0) {
      this.selectedLeafId = Number(node.key);
      this.activeMenuNode = node;
      void this.getMenuPermissionsByNode(node);
    } else {
      this.selectedLeafId = null;
      if (this.readOnly) return;
      this.confirmBranchChange(node);
    }
  }

  async getMenuPermissionsByNode(node: TreeNode): Promise<void> {
    await this.withLoader(async () => {
      const data =
        await this.rolesPermissionsService.getMenuPermissionsByOption(
          Number(this.form.get('appSelect')?.value),
          Number(this.form.get('menuSelect')?.value),
          Number(node.data?.OpcionOriginalId),
        );

      this.currentPermissionsByMenu = data;
      this.initializeMatrix();
    });
  }

  confirmBranchChange(node: TreeNode): void {
    this.confirmationService.confirm({
      message:
        'Si modifica los permisos de esta opción del menú, se perderán los permisos específicos de los elementos hijos. ¿Desea continuar?',
      header: 'Confirmation',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Aceptar',
      },
      accept: () => {
        this.activeMenuNode = node;
        this.currentPermissionsByMenu = [];
        this.initializeMatrix();
      },
      reject: () => {
        this.allSelected = false;
      },
    });
  }

  isActiveOrAncestor(node: TreeNode): boolean {
    if (!this.activeMenuNode) return false;
    if (node.key === this.activeMenuNode.key) return true;

    let parent = node.parent;
    while (parent) {
      if (parent.key === this.activeMenuNode.key) {
        return true;
      }

      parent = parent.parent;
    }

    return false;
  }

  cancelActiveNode(): void {
    this.activeMenuNode = null;
  }

  async savePermissions(): Promise<void> {
    if (this.readOnly) return;

    const payload = this.buildPayloadFromMatrix();
    if (!payload) return;

    let toastMessage: {
      severity: 'success' | 'error';
      summary: string;
      detail: string;
    } | null = null;

    this.startLoading();
    try {
      await this.rolesPermissionsService.saveMenuPermissionsByOptions(payload);

      await this.refreshMenuSelectionState(payload.menuId, payload.appId);

      if (this.selectedLeafId != null && this.selectedLeafId >= 0) {
        if (this.activeMenuNode?.children?.length === 0) {
          await this.getMenuPermissionsByNode(this.activeMenuNode);
        }
      } else {
        this.initializeMatrix();
        this.allSelected = false;
      }

      toastMessage = {
        severity: 'success',
        summary: 'Éxito',
        detail: 'Permisos guardados correctamente',
      };
    } catch (error: unknown) {
      console.error('Error saving menu permissions', error);
      toastMessage = {
        severity: 'error',
        summary: 'Error',
        detail: 'Hubo un error al guardar los permisos',
      };
    } finally {
      this.stopLoading();
    }

    if (toastMessage) {
      const messageToShow = toastMessage;
      setTimeout(() => {
        this.messageService.add(messageToShow);
      }, 0);
    }
  }

  buildPayloadFromMatrix(): any {
    if (!this.activeMenuNode) {
      return null;
    }

    const appId = this.form.get('appSelect')?.value;
    const menuId = this.form.get('menuSelect')?.value;
    const permissions: { IdRol: number; IdPermiso: number }[] = [];
    let nodeIds: number[] = [];

    if (this.allSelected) {
      this.menuTree.forEach((root) => {
        nodeIds = nodeIds.concat(this.getLeafNodeIds(root));
      });
    } else if (
      !this.activeMenuNode.children ||
      this.activeMenuNode.children.length === 0
    ) {
      nodeIds = [Number(this.activeMenuNode.data?.OpcionOriginalId)];
    } else {
      nodeIds = this.getLeafNodeIds(this.activeMenuNode);
    }

    Object.keys(this.permissionMatrix).forEach((roleId) => {
      Object.keys(this.permissionMatrix[roleId]).forEach((permitId) => {
        if (this.permissionMatrix[roleId][permitId]) {
          permissions.push({
            IdRol: Number(roleId),
            IdPermiso: Number(permitId),
          });
        }
      });
    });

    return {
      appId,
      menuId,
      menuOpcionIds: nodeIds,
      permisos: permissions,
    };
  }

  private getLeafNodeIds(node: TreeNode): number[] {
    if (!node.children || node.children.length === 0) {
      return [Number(node.data?.OpcionOriginalId)];
    }

    let leafIds: number[] = [];
    node.children.forEach((child) => {
      leafIds = leafIds.concat(this.getLeafNodeIds(child));
    });

    return leafIds;
  }

  onAllCheckboxChange(event: any): void {
    if (this.readOnly) return;

    if (event.checked) {
      this.confirmBranchChange(this.allNode);
    } else {
      this.activeMenuNode = null;
    }
  }

  private translate(textId: unknown, fallback = ''): string {
    const id =
      typeof textId === 'string' || typeof textId === 'number'
        ? String(textId)
        : '';

    if (!id) return fallback;
    if (!this.translateFn) return fallback || id;
    return this.translateFn(id, fallback || id);
  }
}
